import type { PropsWithChildren } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { useMeasure } from 'react-use';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS, BrandXXL } from '@sorare/core/src/atoms/typography';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';
import { breakpoints, mobileAndAbove } from '@sorare/core/src/style/mediaQuery';

import { ESTIMATED_CASH_PRIZE_IN_USER_CURRENCY } from 'components/commonGame/UnlockPro/ChooseGameMode/LimitedPlusMode';
import cashRewardsBgDesktop from 'components/onboarding/WinItems/assets/cash-rewards-bg-desktop.webp';
import cashRewardsBg from 'components/onboarding/WinItems/assets/cash-rewards-bg.webp';

const Root = styled.div`
  container-type: inline-size;
`;
const InnerRoot = styled(Vertical)`
  position: relative;
`;

const DesktopImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  aspect-ratio: 996 / 460;
`;

const MobileImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  aspect-ratio: 626 / 461;
`;

const TextWrapper = styled.div`
  position: absolute;
  padding-left: 25%;
  @container ${mobileAndAbove} {
    padding-left: 45%;
  }
`;

export const CashRewards = ({ children }: PropsWithChildren) => {
  const { fiatCurrency } = useCurrentUserCurrencies();
  const [ref, { height, width }] = useMeasure<HTMLDivElement>();
  const isMobileAndAbove = width > breakpoints.mobile;
  return (
    <Root ref={ref}>
      <InnerRoot>
        {isMobileAndAbove ? (
          <DesktopImg src={cashRewardsBgDesktop} alt="" />
        ) : (
          <MobileImg src={cashRewardsBg} alt="" />
        )}
        <TextWrapper
          style={{
            paddingTop:
              // achieve height percent-based positioning
              `${height * 0.32}px`,
          }}
        >
          <BrandS>
            <FormattedMessage
              id="TutorialPage.cashRewards"
              defaultMessage="Cash rewards up to{br}<strong>{prize}*</strong>"
              values={{
                br: <br />,
                strong: chunks => (
                  <BrandXXL
                    className="text-gradient-color"
                    style={
                      {
                        '--text-gradient-color':
                          'linear-gradient(218deg, #FFF 1.37%, #FCFF63 36.66%, #C26900 101.57%)',
                      } as CSSProperties
                    }
                  >
                    {chunks}
                  </BrandXXL>
                ),
                prize: (
                  <FormattedNumber
                    style="currency"
                    currency={fiatCurrency}
                    value={ESTIMATED_CASH_PRIZE_IN_USER_CURRENCY}
                    currencyDisplay="narrowSymbol"
                    notation="compact"
                    maximumFractionDigits={0}
                  />
                ),
              }}
            />
          </BrandS>
        </TextWrapper>
        {children}
      </InnerRoot>
    </Root>
  );
};
